// src/types/ImportOrder.ts
//
// Renamed from the old "PurchaseOrder" e-commerce module (see answers.text) —
// this now models how a car dealership actually imports vehicles, which
// splits into two genuinely different acquisition flows:
//
//   'brand_new_batch'  — N interchangeable units of one spec ordered from a
//                         distributor before individual VINs are assigned.
//                         Quantity > 1 is normal.
//   'used_individual'  — one specific vehicle sourced from an auction/broker,
//                         with its own condition, mileage and inspection
//                         history. Quantity is always 1.
//
// Both used to be modeled as "pick an existing catalog Product, then enter a
// quantity" — which required the car to already have a listing before you
// could import it, and reduced a used car's real-world history to a bare
// quantity field. Items now carry the vehicle SPEC (brand/model/trim/
// category) instead of an existing Product reference, and receiving an item
// creates real Car records — see importOrderService.ts,
// markImportOrderAsReceived.

export enum ImportOrderStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  APPROVED = 'approved',
  ORDERED = 'ordered',
  RECEIVED = 'received',
  CANCELLED = 'cancelled',
}

export enum PaymentStatus {
  UNPAID = 'unpaid',
  PARTIAL = 'partial',
  PAID = 'paid',
}

export enum ReturnStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

// Vehicle/import-appropriate reasons — replaces the old generic retail-
// restock reasons (damaged/defective/wrong_item/quality_issue/EXPIRED/other),
// none of which fit returning a vehicle to a supplier/broker/shipper.
export enum ReturnReason {
  FAILED_INSPECTION = 'failed_inspection',
  UNDISCLOSED_DAMAGE = 'undisclosed_damage',
  DOCUMENTATION_MISMATCH = 'documentation_mismatch',
  WRONG_SPEC_SHIPPED = 'wrong_spec_shipped',
  MECHANICAL_FAULT = 'mechanical_fault',
  CUSTOMS_ISSUE = 'customs_issue',
  OTHER = 'other',
}

export type AcquisitionType = 'brand_new_batch' | 'used_individual';

// ── Additional landed-cost taxes & Letter of Credit / document tracking ──────
// Additive to the existing taxRate/taxAmount/shippingCost — those keep working
// exactly as before. cifValue is the customs-declared Cost+Insurance+Freight
// value, kept for reference only and intentionally excluded from totalAmount.
// @deprecated — superseded by ImportOrderCost / the `costs` field below. Kept
// only so older stored orders (created before the cost rework) still type-check.
export type OtherTax = { id: string; name: string; amount: number };

// ── Flexible landed cost line (replaces the old fixed tax rate / shipping /
// CIF / customs / VAT / luxury tax fields — see ImportOrder.costs below).
// Each line is either a flat currency amount, or a percentage of the items
// subtotal (e.g. a 5% customs duty automatically tracks subtotal changes). ──
export type CostAmountType = 'fixed' | 'percentage';

export interface ImportOrderCost {
  id: string;
  name: string;
  amountType: CostAmountType;
  // Currency amount when amountType is 'fixed'; percentage points (0-100 of
  // the items subtotal) when amountType is 'percentage'.
  value: number;
}

export const computeCostAmount = (cost: ImportOrderCost, subtotal: number): number =>
  cost.amountType === 'percentage' ? subtotal * ((cost.value || 0) / 100) : (cost.value || 0);

export const sumCosts = (costs: ImportOrderCost[] | undefined, subtotal: number): number =>
  (costs || []).reduce((sum, c) => sum + computeCostAmount(c, subtotal), 0);

// One entry per status transition — who changed it and when. Recorded on
// every status change (see ImportOrderService.updateImportOrderStatus /
// markImportOrderAsReceived), not just approval.
export interface ImportOrderStatusHistoryEntry {
  status: ImportOrderStatus;
  changedBy: string;
  changedByName: string;
  changedAt: Date;
}

// One entry per payment recorded against the order — replaces the old
// "just bump a running total" approach so payment dates are preserved.
export interface ImportOrderPayment {
  id: string;
  amount: number;
  paidAt: Date;
  recordedBy: string;
  recordedByName: string;
}

export type LcStatus = 'Not Opened' | 'Opened' | 'Closed';

export type ImportOrderDocumentType =
  | 'LC Copy'
  | 'Invoice'
  | 'Bill of Lading'
  | 'Customs Declaration'
  | 'Other';

export interface ImportOrderDocument {
  id: string;
  name: string;
  type: ImportOrderDocumentType;
  url: string;
  uploadedAt: Date;
}

export interface ImportOrderItem {
  id?: string;

  acquisitionType: AcquisitionType;

  // ── Vehicle spec — required for both acquisition types ──────────────────
  brandId: string;
  brandName: string;
  modelId: string;
  modelName: string;
  trimId?: string;
  trimName?: string;
  categoryId: string;   // body type — required to create the Car record on receipt
  categoryName: string;

  // ── Batch fields ──────────────────────────────────────────────────────
  // quantity is always 1 for 'used_individual'.
  quantity: number;
  unitPrice: number;    // landed cost per unit — never the selling price
  totalPrice: number;
  receivedQuantity: number;

  // ── Destination — for 'brand_new_batch' this is filled in per-unit at
  // receive time (see the warehouse-split allocation in ImportOrderViewDialog);
  // for 'used_individual' it's chosen once, since there's only one unit.
  warehouseId?: string;
  warehouseName?: string;

  // ── Used / individual only — the real-world facts that matter for a
  // specific pre-owned vehicle, none of which existed on the old generic
  // restock line item. ──
  vin?: string;
  condition?: string;       // a CarCondition value, e.g. 'Used - Good'
  modelYear?: number;
  mileageKm?: number;
  exteriorColor?: string;
  inspectionNotes?: string;

  // Car record(s) generated from this line once received (one per unit).
  carIds?: string[];

  notes?: string;
}

export interface ReturnItem {
  id?: string;
  // The specific vehicle being returned/disputed — a used-individual return
  // is always exactly one Car; a brand-new-batch return can affect several
  // interchangeable units at once (see quantity below).
  carId?: string;
  brandName: string;
  modelName: string;
  trimName?: string;
  vin?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  reason: ReturnReason;
  notes?: string;
  warehouseId?: string;
  warehouseName?: string;
  images?: string[]; // evidence photos / inspection report scans
}

export interface ImportOrderReturn {
  id?: string;
  returnNumber: string;
  importOrderId: string;
  importOrderNumber: string;
  supplierId: string;
  supplierName: string;
  supplierEmail: string;
  supplierPhone: string;
  status: ReturnStatus;
  items: ReturnItem[];
  totalAmount: number;
  refundAmount: number;
  refundIssued: boolean;
  reason: string;
  notes?: string;
  internalNotes?: string;
  images?: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  completedBy?: string;
  completedByName?: string;
  completedAt?: Date;
  inventoryAdjusted?: boolean;
}

export interface ImportOrderReturnFormData {
  importOrderId: string;
  items: ReturnItem[];
  reason: string;
  notes: string;
  internalNotes: string;
  images?: File[];
}

export interface ReturnStats {
  total: number;
  pending: number;
  approved: number;
  completed: number;
  rejected: number;
  cancelled: number;
  totalValue: number;
  totalRefunded: number;
}

export interface Supplier {
  id?: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  contactPerson?: string;
  taxId?: string;
  paymentTerms?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ImportOrder {
  id?: string;
  poNumber: string;
  supplierId: string;
  supplierName: string;
  supplierEmail: string;
  supplierPhone: string;
  status: ImportOrderStatus;
  paymentStatus: PaymentStatus;
  items: ImportOrderItem[];
  subtotal: number;
  totalAmount: number;
  paidAmount: number;
  expectedDeliveryDate?: Date;
  actualDeliveryDate?: Date;
  notes?: string;
  internalNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  receivedBy?: string;
  receivedByName?: string;
  receivedAt?: Date;
  // Flag to track if the Car records for this order have been generated
  stockUpdated?: boolean;
  returnIds?: string[];

  // ── Flexible landed costs (current model) — see ImportOrderCost above.
  // New/edited orders write here; totalAmount = subtotal + sumCosts(costs, subtotal). ──
  costs?: ImportOrderCost[];

  // @deprecated legacy landed-cost fields — still present on orders created
  // before the cost rework so their historical totals stay readable. Not
  // written by the form anymore; ImportOrderFormPage migrates these into
  // `costs` the first time such an order is opened for editing.
  taxRate?: number;
  taxAmount?: number;
  shippingCost?: number;
  cifValue?: number;
  customsDuty?: number;
  vatAmount?: number;
  luxuryTax?: number;
  otherTaxes?: OtherTax[];

  // ── Status change history — one entry per transition, who and when. ──
  statusHistory?: ImportOrderStatusHistoryEntry[];

  // ── Payment history — one entry per recorded payment, replaces the old
  // "just bump paidAmount" approach so dates aren't lost. ──
  payments?: ImportOrderPayment[];

  // ── Letter of Credit tracking — independent of the order's status workflow,
  // since LC status typically changes after the order has left DRAFT. ──
  lcNumber?: string;
  lcStatus?: LcStatus;
  lcOpenedDate?: Date;

  // ── Import paperwork (LC copy, invoice, bill of lading, etc.) ──
  documents?: ImportOrderDocument[];
}

export interface ImportOrderFormData {
  supplierId: string;
  items: ImportOrderItem[];
  expectedDeliveryDate: Date | null;
  notes: string;
  internalNotes: string;

  // ── Flexible landed costs — replaces the old fixed tax rate / shipping /
  // CIF / customs / VAT / luxury tax fields. ──
  costs: ImportOrderCost[];

  // ── Letter of Credit ──
  lcNumber: string;
  lcStatus: LcStatus;
  lcOpenedDate: Date | null;
}

export interface ImportOrderStats {
  total: number;
  draft: number;
  pending: number;
  approved: number;
  ordered: number;
  received: number;
  cancelled: number;
  totalValue: number;
  totalPaid: number;
  totalOutstanding: number;
}

export const IMPORT_ORDER_STATUS_COLORS: Record<ImportOrderStatus, string> = {
  [ImportOrderStatus.DRAFT]: 'default',
  [ImportOrderStatus.PENDING]: 'warning',
  [ImportOrderStatus.APPROVED]: 'info',
  [ImportOrderStatus.ORDERED]: 'primary',
  [ImportOrderStatus.RECEIVED]: 'success',
  [ImportOrderStatus.CANCELLED]: 'error',
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  [PaymentStatus.UNPAID]: 'error',
  [PaymentStatus.PARTIAL]: 'warning',
  [PaymentStatus.PAID]: 'success',
};

export const RETURN_STATUS_COLORS: Record<ReturnStatus, string> = {
  [ReturnStatus.PENDING]: 'warning',
  [ReturnStatus.APPROVED]: 'info',
  [ReturnStatus.REJECTED]: 'error',
  [ReturnStatus.COMPLETED]: 'success',
  [ReturnStatus.CANCELLED]: 'default',
};

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  [ReturnReason.FAILED_INSPECTION]: 'Failed Pre-Registration Inspection',
  [ReturnReason.UNDISCLOSED_DAMAGE]: 'Undisclosed Accident / Damage',
  [ReturnReason.DOCUMENTATION_MISMATCH]: 'Documentation / Odometer Mismatch',
  [ReturnReason.WRONG_SPEC_SHIPPED]: 'Wrong Spec / Trim Shipped',
  [ReturnReason.MECHANICAL_FAULT]: 'Mechanical Fault on Inspection',
  [ReturnReason.CUSTOMS_ISSUE]: 'Customs / Import Documentation Issue',
  [ReturnReason.OTHER]: 'Other',
};
